import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { AppRoute } from '@common/app/routes';
import { Layout } from '@components/Layout/Layout';
import Production from '@components/Materials/Production/Production';
import Friends from '@pages/Friends/Friends';
import Goods from '@pages/Goods/Goods';
import Home from '@pages/Home/Home';
import { FactoryBuild } from '@pages/Materials/FactoryBuild/FactoryBuild';
import { FactoryUpgrade } from '@pages/Materials/FactoryUpgrade/FactoryUpgrade';
import Materials from '@pages/Materials/Materials';
import Partners from '@pages/Partners/Partners';
import { QuarryBuild } from '@pages/Resources/QuarryBuild/QuarryBuild';
import { QuarryUpgrade } from '@pages/Resources/QuarryUpgrade/QuarryUpgrade';
import Resources from '@pages/Resources/Resources';
import ChangeLanguage from '@pages/Settings/changeLanguage/ChangeLanguage';
import ChangeSkin from '@pages/Settings/changeSkin/ChangeSkin';
import PushSettings from '@pages/Settings/pushSettings/PushSettings';
import Settings from '@pages/Settings/Settings';
import { Storage } from '@pages/Storage/Storage';
import Task from '@pages/Task/Task';
import { WorkshopBuild } from '@pages/Workshop/WorkshopBuild/WorkshopBuild';
import { WorkshopUpgrade } from '@pages/Workshop/WorkshopUpgrade/WorkshopUpgrade';

import Workshop from '@/pages/Workshop/WorkshopPage';
import { type RootState } from '@/redux/store';

// import './telegramAnalytics';
import { ProtectedRoute } from './components/ProtectedRoute/ProtectedRoute';
import { AuthCallback } from './pages/Auth/AuthCallback/AuthCallback';
import { Login } from './pages/Auth/Login/Login';
import { Bingo } from './pages/Bingo/Bingo';
import { BingoLeaderboard } from './pages/BingoLeaderboard/BingoLeaderboard';
import { DiceGame } from './pages/DIceGame/DiceGame';
import ErrorPage from './pages/ErrorPage/ErrorPage';
import { GameCenter } from './pages/GameCenter/GameCenter';
import { Leagues } from './pages/Leagues/Leagues';
import { LotteryFaq } from './pages/Lottery/LotteryFaq/LotteryFaq';
import { LotteryHistory } from './pages/Lottery/LotteryHistory/LotteryHistory';
import { LotteryLayout } from './pages/Lottery/LotteryLayout';
import { LotteryMain } from './pages/Lottery/LotteryMain/LotteryMain';
import MaintenancePage from './pages/Maintenance/MaintenancePage';
import Notifications from './pages/Notifications';
import { Onboarding } from './pages/Onboarding/Onboarding';
import { Privacy } from './pages/Privacy/Privacy';
import { Quarry } from './pages/Resources/Quarry/Quarry';
import { Scratch } from './pages/Scratch/Scratch';
import { Shop } from './pages/Shop/Shop';
import { Terms } from './pages/Terms/Terms';

import './App.scss';

const App: React.FC = () => {
  const maintenanceEndTime = useSelector(
    (state: RootState) => state.user.maintenanceEndTime,
  );

  if (maintenanceEndTime) {
    return <MaintenancePage />;
  }

  return (
    <Routes>
      <Route
        path={AppRoute.MAIN}
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <>
              <Onboarding />
              <Home />
            </>
          }
        />
        <Route path={AppRoute.RATING} element={<div></div>} />
        <Route path={AppRoute.STOCK} element={<Storage />} />
        <Route path={AppRoute.RESOURCES}>
          <Route index element={<Resources />} />
          <Route path=":id/upgrade" element={<QuarryUpgrade />} />
          <Route path="mineResource/:id" element={<Quarry />} />
          <Route path="build" element={<QuarryBuild />} />
        </Route>
        <Route path={AppRoute.EARN} element={<Task />} />
        <Route
          path={AppRoute.PARTNERS}
          element={
            Capacitor.isNativePlatform() ? (
              <Navigate to={AppRoute.MAIN} replace />
            ) : (
              <Partners />
            )
          }
        />
        <Route path={AppRoute.SETTINGS} element={<Settings />}>
          <Route path="changeLanguage" element={<ChangeLanguage />} />
          <Route path="changeSkin" element={<ChangeSkin />} />
          <Route path="pushSettings" element={<PushSettings />} />
        </Route>
        <Route path={AppRoute.FACTORY}>
          <Route index element={<Materials />} />
          <Route path=":id" element={<Production />} />
          <Route path=":id/upgrade" element={<FactoryUpgrade />} />
          <Route path="build" element={<FactoryBuild />} />
        </Route>
        <Route path={AppRoute.WORKSHOP}>
          <Route index element={<Goods />} />
          <Route path=":id" element={<Workshop />} />
          <Route path=":id/upgrade" element={<WorkshopUpgrade />} />
          <Route path="build" element={<WorkshopBuild />} />
        </Route>
        <Route path={AppRoute.FRIENDS} element={<Friends />} />
        <Route path={AppRoute.TERMS} element={<Terms />} />
        <Route path={AppRoute.PRIVACY} element={<Privacy />} />
        <Route path={AppRoute.LEAGUES} element={<Leagues />} />
        <Route path={AppRoute.ERRORPAGE} element={<ErrorPage />} />
        <Route path={AppRoute.SHOP} element={<Shop />} />
        <Route path={AppRoute.DICE_GAME} element={<DiceGame />} />
        <Route path={AppRoute.GAME_CENTER} element={<GameCenter />} />
        <Route path={AppRoute.SCRATCH} element={<Scratch />} />
        <Route path={AppRoute.BINGO} element={<Bingo />} />
        <Route
          path={AppRoute.BINGO_LEADERBOARD}
          element={<BingoLeaderboard />}
        />
        <Route path={AppRoute.NOTIFICATIONS} element={<Notifications />} />
        <Route path={AppRoute.LOTTERY} element={<LotteryLayout />}>
          <Route index element={<LotteryMain />} />
          <Route path="history" element={<LotteryHistory />} />
          <Route path="faq" element={<LotteryFaq />} />
        </Route>
      </Route>
      <Route path={AppRoute.AUTH_CALLBACK} element={<AuthCallback />} />
      <Route path={AppRoute.LOGIN} element={<Login />} />
    </Routes>
  );
};

export default App;
